import React, { useState, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Brand } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { RecordSlideOver } from "@/components/RecordSlideOver";
import { NotificationBell } from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, FileText, Landmark, Bell, BarChart3, Settings as SettingsIcon,
  Plus, LogOut,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Overview", Icon: LayoutDashboard, end: true },
  { to: "/dashboard/invoices", label: "Invoices", Icon: FileText },
  { to: "/dashboard/payouts", label: "Payouts", Icon: Landmark },
  { to: "/dashboard/notifications", label: "Notifications", Icon: Bell },
  { to: "/dashboard/reports", label: "Reports", Icon: BarChart3 },
  { to: "/dashboard/settings", label: "Settings", Icon: SettingsIcon },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [slideOpen, setSlideOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  const openCreate = useCallback((rec = null) => { setEditRecord(rec); setSlideOpen(true); }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };
  const initials = (user?.full_name || user?.email || "U").slice(0, 1).toUpperCase();

  return (
    <div className="min-h-screen bg-cream">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r bg-white">
        <div className="h-16 flex items-center px-6 border-b"><Brand /></div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`nav-${label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                  isActive ? "bg-brandblue-light text-brandblue" : "text-midnight/60 hover:bg-oatmeal hover:text-midnight"}`}>
              <Icon size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t">
          <button onClick={doLogout} data-testid="sidebar-logout"
            className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-espresso-soft hover:bg-oatmeal w-full">
            <LogOut size={18} /> Log out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="h-16 sticky top-0 z-30 bg-white/90 backdrop-blur border-b flex items-center justify-between px-4 sm:px-8">
          <div className="lg:hidden"><Brand /></div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <Button onClick={() => openCreate(null)} data-testid="new-record-btn" className="gap-1.5">
              <Plus size={16} /> New record
            </Button>
            <NotificationBell refreshKey={refreshKey} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-9 h-9 rounded-full bg-midnight text-white grid place-items-center text-sm font-semibold" data-testid="account-menu">
                  {initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-semibold truncate">{user?.full_name || "You"}</p>
                  <p className="text-espresso-soft truncate text-xs">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>Settings</DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout">Log out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="px-4 sm:px-8 py-8 pb-28 lg:pb-8 max-w-6xl">
          <Outlet context={{ openCreate, triggerRefresh, refreshKey, user }} />
        </main>
      </div>

      {/* Bottom nav (mobile) */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t grid grid-cols-5">
        {NAV.filter((n) => n.label !== "Settings").map(({ to, label, Icon, end }) => (
          <NavLink key={to} to={to} end={end} data-testid={`bottomnav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors ${
                isActive ? "text-brandblue" : "text-ink-muted"}`}>
            <Icon size={20} /> {label}
          </NavLink>
        ))}
      </nav>

      <RecordSlideOver open={slideOpen} onOpenChange={setSlideOpen} editRecord={editRecord}
        onCreated={triggerRefresh} />
    </div>
  );
}
